import { AdminGuard } from '@/components/admin-guard'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { KbEditor } from '@/features/kb/KbEditor'
import { KbReviewPanel } from '@/features/kb/KbReviewPanel'
import { useUpdateKbArticle, kbKeys } from '@/queries/kb'
import { FullPageSpinner } from '@/components/etainabl-spinner'
import type { KbArticle } from '@/types/kb'

// A short fingerprint of what the editor shows.
function textKey(a: KbArticle) {
  const text = `${a.title}\u0000${a.subtitle}\u0000${a.content}`
  const hash = [...text].reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 0)
  return `${text.length}:${hash}`
}

function EditArticle() {
  const { id } = Route.useParams()
  const update = useUpdateKbArticle(id)

  // Fetch by id directly (the public lookup is by slug). We fetch the list filtered by _id is not supported;
  // simplest path: just fetch the list and find the article — but for performance we use the slug lookup
  // only if we know it. Since the editor entry is /admin/kb/$id, we don't have the slug yet, so we need
  // an admin-only "by id" lookup. For now: list with a tight filter.
  const articleQuery = useQuery({
    queryKey: kbKeys.byId(id),
    queryFn: async () => {
      const res = await api<{ data: KbArticle[] }>('/kb-articles', { query: { _id: id, limit: 1 } })
      return res.data[0]
    },
  })

  if (articleQuery.isLoading) return <FullPageSpinner />
  if (articleQuery.error || !articleQuery.data) return <div className="p-6">Article not found.</div>

  return (
    <>
      <KbReviewPanel article={articleQuery.data} />
      {/* Keyed by the article's text, so a revert or a rewrite reloads the editor's fields, but marking it reviewed (which
          changes nothing in them) keeps any edits not yet saved. */}
      <KbEditor
      key={textKey(articleQuery.data)}
      initial={articleQuery.data}
      articleId={id}
      saving={update.isPending}
      onSave={async values => {
        try {
          await update.mutateAsync(values)
          toast.success('Article saved')
        } catch (err) {
          toast.error((err as Error).message ?? 'Could not save')
        }
      }}
      />
    </>
  )
}

export const Route = createFileRoute('/admin/kb/$id')({ component: () => (
    <AdminGuard>
      <EditArticle />
    </AdminGuard>
  ) })
